import type {
  RecommendationParameterSchema,
  BaseRecommendationWithUserUpdatedAt,
  BaseRecommendationSchema,
  RecommendationSchema,
} from '~/types/schema';
import type { NomadDB } from '~/db/NomadDB';
import { getServices } from '~/db/services';
import type { AdaptiveParameters } from '~/types/app';

export interface ScoreRecommendation {
  scores: { historicScore: number; recommendationScore: number };
  recommendationItem: BaseRecommendationSchema;
}

export interface ITypedItem {
  id: string;
  type: string;
}

export const getTypedId = (item: ITypedItem): string => {
  return `${item.type}-${item.id}`;
};

export const getMapRecommendationParameters = async (
  db: NomadDB,
  items: ITypedItem[]
): Promise<Map<string, RecommendationParameterSchema>> => {
  const typesAndsIds = items.map((item) => [item.type, item.id]);

  const { recommendationParametersService } = getServices(db);

  const recommendationParameters =
    await recommendationParametersService.findByTypesAndIds(typesAndsIds);

  const mRecommendationParameters = new Map<
    string,
    RecommendationParameterSchema
  >();

  recommendationParameters.forEach((params) => {
    mRecommendationParameters.set(getTypedId(params), params);
  });

  return mRecommendationParameters;
};

export const getRecommendationLevelItems = (
  recommendation: RecommendationSchema,
  exerciseScore: number,
  gradeThresholds: {
    high: number;
    low: number;
  }
) => {
  if (exerciseScore <= gradeThresholds.low) {
    return [
      ...recommendation.recommendations.low,
      ...recommendation.recommendations.mid,
    ];
  } else if (exerciseScore >= gradeThresholds.high) {
    return [
      ...recommendation.recommendations.mid,
      ...recommendation.recommendations.high,
    ];
  } else {
    return [...recommendation.recommendations.mid];
  }
};

export const getRecommendationLevelItemsByExercises = (
  recommendations: RecommendationSchema[],
  progressionInfosByExercises: Map<
    string,
    { score: number; userUpdatedAt: string }
  >,
  gradeThresholds: {
    high: number;
    low: number;
  }
) => {
  let items: BaseRecommendationWithUserUpdatedAt[] = [];

  recommendations.forEach((recommendation) => {
    const progressionInfos = progressionInfosByExercises.get(recommendation.id);
    if (progressionInfos) {
      let newItems = getRecommendationLevelItems(
        recommendation,
        progressionInfos.score,
        gradeThresholds
      );
      newItems = newItems.map((item) => ({
        ...item,
        userUpdatedAt: progressionInfos.userUpdatedAt,
      }));
      items = [...items, ...newItems];
    }
  });

  return items;
};

export const nbDaysBtw = (
  pDate: string,
  adaptiveParameters: AdaptiveParameters
): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  const now = new Date().getTime();
  const lastDate = new Date(pDate).getTime();
  const diffDays = Math.round(Math.abs((now - lastDate) / oneDay));

  if (diffDays === 0) return 1;
  const timeConstant1 = adaptiveParameters.historicModule.timeConst1;
  const timeConstant2 = adaptiveParameters.historicModule.timeConst2;
  const result =
    timeConstant1 /
    (Math.pow(Math.log10(diffDays), timeConstant2) + timeConstant1);

  return result;
};

export const computeHistoricScore = (
  adaptiveParameters: AdaptiveParameters,
  recommendationOpenedDate?: string
): number => {
  if (!recommendationOpenedDate) return 0;

  const decayRate = adaptiveParameters.historicModule.decayRate;
  const scoreNouveaute = Math.exp(
    -nbDaysBtw(recommendationOpenedDate, adaptiveParameters) * decayRate
  );
  return 1 - scoreNouveaute;
};

export const getScoreRecommendation = (
  recommendationParameter: RecommendationParameterSchema,
  recommendationItem: BaseRecommendationSchema,
  adaptiveParameters: AdaptiveParameters
): { historicScore: number; recommendationScore: number } => {
  const recommendationOpenedAt =
    recommendationParameter?.recommendationOpenedAt;

  const educationalScore = recommendationItem.educationalScore;

  const historicScore: number = computeHistoricScore(
    adaptiveParameters,
    recommendationOpenedAt
  );

  const wNew = adaptiveParameters.modulesWeights.wNew;
  const wPedago = adaptiveParameters.modulesWeights.wPedago;
  const currentEducationLevel = adaptiveParameters.educationLevel;

  const adjustedEducationalLevel = Math.abs(
    (currentEducationLevel - recommendationItem.educationLevel) *
      adaptiveParameters.modulesWeights.wLevel
  );

  const recommendationScore =
    wPedago * educationalScore -
    wNew * historicScore -
    adjustedEducationalLevel;

  return { historicScore, recommendationScore };
};

export const getSortedAndCleanedScoreRecommendations = (
  recommendationItems: BaseRecommendationSchema[],
  mRecommendationParameters: Map<string, RecommendationParameterSchema>,
  adaptiveParameters: AdaptiveParameters
) => {
  let scoreRecommendations: ScoreRecommendation[] = recommendationItems.reduce(
    (scoreRecommendations, recommendationItem) => {
      const scores = getScoreRecommendation(
        mRecommendationParameters.get(getTypedId(recommendationItem)),
        recommendationItem,
        adaptiveParameters
      );
      scoreRecommendations.push({
        scores,
        recommendationItem,
      });
      return scoreRecommendations;
    },
    []
  );

  const setItems = new Set();

  scoreRecommendations.sort(
    (item1, item2) =>
      item2.scores.recommendationScore - item1.scores.recommendationScore
  );

  scoreRecommendations = scoreRecommendations.filter((item) => {
    const id = getTypedId(item.recommendationItem);
    if (!setItems.has(id)) {
      setItems.add(id);
      return true;
    }
    return false;
  });

  return scoreRecommendations;
};

export const getMapRecommendationParametersFromItems = async (
  db: NomadDB,
  recommendedItems: BaseRecommendationSchema[]
): Promise<Map<string, RecommendationParameterSchema>> => {
  const mRecommendationParameters = await getMapRecommendationParameters(
    db,
    recommendedItems
  );

  return mRecommendationParameters;
};

export const saveBaseInfosRecommendationParameters = async (
  db: NomadDB,
  scoreRecommendations: ScoreRecommendation[],
  disciplineId: string
) => {
  const items = scoreRecommendations.map(
    (scoreRecommendation) => scoreRecommendation.recommendationItem
  );
  const mRecommendationParameters = await getMapRecommendationParameters(
    db,
    items
  );
  const recommendationParameters = scoreRecommendations.map((item) => {
    const id = getTypedId(item.recommendationItem);

    const rp = mRecommendationParameters.has(id)
      ? mRecommendationParameters.get(id)
      : ({
          id: item.recommendationItem.id,
          type: item.recommendationItem.type, // type de l'objet (category|exercise|post)
          disciplineId, // id de la discipline de l'origine de la recommendation
        } as RecommendationParameterSchema);
    rp.recommendationScore = item.scores.recommendationScore;
    rp.recommendingContentEndedAt = item.recommendationItem.userUpdatedAt;
    rp.local = 1;
    return rp;
  });
  const { recommendationParametersService } = getServices(db);
  await recommendationParametersService.bulkPut(recommendationParameters);
};

export const getRecommendationParameter = async (
  db: NomadDB,
  type: string,
  id: string
): Promise<RecommendationParameterSchema> => {
  const { recommendationParametersService } = getServices(db);
  let recommendationParameter = await recommendationParametersService.get([
    type,
    id,
  ]);
  if (!recommendationParameter) {
    recommendationParameter = {
      id,
      type,
    } as RecommendationParameterSchema;
  }
  return recommendationParameter;
};

export const saveRecommendationParameter = async (
  db: NomadDB,
  data: RecommendationParameterSchema
): Promise<void> => {
  const { recommendationParametersService } = getServices(db);
  await recommendationParametersService.put({ ...data, local: 1 });
};

export const getAndSaveRecommendationParameter = async (
  db: NomadDB,
  type: string,
  id: string,
  data
): Promise<void> => {
  const recommendationParameter = await getRecommendationParameter(
    db,
    type,
    id
  );

  await saveRecommendationParameter(db, {
    ...recommendationParameter,
    ...data,
  });
};

export const saveRecommendationOpenedAt = async (
  db: NomadDB,
  type: string,
  id: string
) => {
  await getAndSaveRecommendationParameter(db, type, id, {
    recommendationOpenedAt: getToDayDateIso(),
  });
};

export const saveRecommendationPoints = async (
  db: NomadDB,
  type: string,
  id: string,
  points?: number,
  maxPoints?: number
) => {
  await getAndSaveRecommendationParameter(db, type, id, {
    points,
    maxPoints,
  });
};

export const saveRecommendationVisited = async (
  db: NomadDB,
  type: string,
  id: string
) => {
  await getAndSaveRecommendationParameter(db, type, id, {
    recommendationOpenedAt: getToDayDateIso(),
    recommendingContentEndedAt: getToDayDateIso(),
  });
};
